/**
 * Pure URL logic for /tools/utm-builder. No DOM, no network — the widget calls
 * these and the unit test (tests/unit/utm-builder-url.test.ts) pins them.
 *
 * Two rules drive everything here:
 *
 * 1. The base URL is split by hand rather than run through `new URL()`. The
 *    URL constructor rewrites what it is given (adds a trailing slash, re-encodes
 *    the existing query, lowercases the host), and a builder that silently edits
 *    the landing page someone pasted is worse than one that leaves it alone.
 * 2. Platform macros are never percent-encoded or case-folded. `{{campaign.name}}`
 *    encoded to `%7B%7Bcampaign.name%7D%7D` is a literal string to Meta and will
 *    reach analytics unexpanded; LinkedIn and TikTok macros must stay uppercase.
 */

export type SpaceMode = 'underscore' | 'hyphen' | 'encode'

export type BuildOptions = {
  lowercase: boolean
  spaces: SpaceMode
}

export type ParamRow = { key: string; value: string }

export type Warning = {
  level: 'error' | 'warn' | 'info'
  message: string
}

export type BuildResult = {
  /** Full landing URL with parameters, or '' when there is no base URL. */
  url: string
  /** Just the new parameters, for fields such as Meta's "URL parameters" or a Google Ads final URL suffix. */
  query: string
  warnings: Warning[]
}

export const STANDARD_UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const

/** Listed on GA4's URL builders help page (answer 10917952), verified 2026-10-12. */
export const GA4_EXTRA_UTM_KEYS = [
  'utm_id',
  'utm_source_platform',
  'utm_creative_format',
  'utm_marketing_tactic',
] as const

import { UTM_PRESETS } from './utm-presets'

/**
 * Macro syntaxes, by family. Order matters: `{{…}}` must be tried before
 * `{…}` so a Meta macro is not read as a ValueTrack one.
 */
const MACRO_PATTERN = /(\{\{[A-Za-z0-9_.]+\}\}|\{[A-Za-z0-9_]+(?::[^{}]*)?\}|__[A-Z0-9_]+__)/g

export type MacroFamily = 'double-brace' | 'single-brace' | 'underscore'

export function macroFamily(token: string): MacroFamily {
  if (token.startsWith('{{')) return 'double-brace'
  if (token.startsWith('{')) return 'single-brace'
  return 'underscore'
}

export function findMacros(value: string): string[] {
  return value.match(MACRO_PATTERN) ?? []
}

/** `{keyword:default}` and `{keyword}` are the same macro; compare without the default. */
function macroName(token: string): string {
  return token.replace(/:[^{}]*\}$/, '}')
}

/** Which preset platforms document each macro (Meta and LinkedIn share `{{…}}`, Google and Microsoft share `{…}`). */
const MACRO_OWNERS: Map<string, Set<string>> = (() => {
  const owners = new Map<string, Set<string>>()
  for (const preset of UTM_PRESETS) {
    for (const { macro } of preset.macros) {
      const name = macroName(macro)
      if (!owners.has(name)) owners.set(name, new Set())
      owners.get(name)!.add(preset.id)
    }
  }
  return owners
})()

/** Split a value into literal text and macro tokens, in order. */
function segments(value: string): { text: string; macro: boolean }[] {
  const out: { text: string; macro: boolean }[] = []
  let last = 0
  for (const match of value.matchAll(MACRO_PATTERN)) {
    const start = match.index ?? 0
    if (start > last) out.push({ text: value.slice(last, start), macro: false })
    out.push({ text: match[0], macro: true })
    last = start + match[0].length
  }
  if (last < value.length) out.push({ text: value.slice(last), macro: false })
  return out
}

/** Apply the case and space rules to literal text only. */
export function normalizeValue(value: string, options: BuildOptions): string {
  return segments(value.trim())
    .map(seg => {
      if (seg.macro) return seg.text
      let text = seg.text
      if (options.lowercase) text = text.toLowerCase()
      if (options.spaces === 'underscore') text = text.replace(/\s+/g, '_')
      else if (options.spaces === 'hyphen') text = text.replace(/\s+/g, '-')
      return text
    })
    .join('')
}

/** Percent-encode literal text, leave macro tokens exactly as typed. */
export function encodeValue(value: string): string {
  return segments(value)
    .map(seg => (seg.macro ? seg.text : encodeURIComponent(seg.text)))
    .join('')
}

function safeDecode(text: string): string {
  try {
    return decodeURIComponent(text.replace(/\+/g, ' '))
  } catch {
    return text
  }
}

export type SplitUrl = {
  /** Everything before `?` (or `#`). */
  path: string
  /** Raw `key=value` pairs from the existing query, untouched. */
  pairs: string[]
  /** Including the leading `#`, or ''. */
  fragment: string
}

export function splitUrl(raw: string): SplitUrl {
  const hashAt = raw.indexOf('#')
  const fragment = hashAt === -1 ? '' : raw.slice(hashAt)
  const beforeHash = hashAt === -1 ? raw : raw.slice(0, hashAt)
  const queryAt = beforeHash.indexOf('?')
  const path = queryAt === -1 ? beforeHash : beforeHash.slice(0, queryAt)
  const query = queryAt === -1 ? '' : beforeHash.slice(queryAt + 1)
  const pairs = query.split('&').filter(pair => pair.length > 0)
  return { path, pairs, fragment }
}

export function pairKey(pair: string): string {
  const eq = pair.indexOf('=')
  return safeDecode(eq === -1 ? pair : pair.slice(0, eq)).trim().toLowerCase()
}

const CLICK_ID_KEYS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid', 'ttclid', 'li_fat_id']

const KEY_PATTERN = /^[A-Za-z0-9_.-]+$/

export function buildUtmUrl(
  baseUrl: string,
  rows: ParamRow[],
  options: BuildOptions,
): BuildResult {
  const warnings: Warning[] = []
  const base = baseUrl.trim()

  // --- the parameters we are adding -------------------------------------
  const filled = rows
    .map(row => ({ key: row.key.trim(), value: row.value }))
    .filter(row => row.key.length > 0 && row.value.trim().length > 0)

  const seen = new Map<string, number>()
  for (const row of filled) {
    const key = row.key.toLowerCase()
    seen.set(key, (seen.get(key) ?? 0) + 1)
    if (!KEY_PATTERN.test(row.key)) {
      warnings.push({
        level: 'warn',
        message: `“${row.key}” contains characters other than letters, digits, _ . and -. Most platforms reject or rewrite keys like that.`,
      })
    }
    if (row.key !== row.key.toLowerCase() && row.key.toLowerCase().startsWith('utm_')) {
      warnings.push({
        level: 'warn',
        message: `“${row.key}” is not lowercase. Analytics tools read utm_ keys in lowercase, so this one may not be recognised.`,
      })
    }
  }
  for (const [key, count] of seen) {
    if (count > 1) {
      warnings.push({
        level: 'error',
        message: `${key} is set ${count} times. Each key should appear once; when it repeats, which copy your analytics keeps is up to the tool.`,
      })
    }
  }

  // A key already on the pasted landing page is kept, so it counts as present.
  const baseKeys = new Set(base ? splitUrl(base).pairs.map(pairKey) : [])
  for (const required of ['utm_source', 'utm_medium', 'utm_campaign']) {
    if (!seen.has(required) && !baseKeys.has(required)) {
      warnings.push({
        level: 'error',
        message: `Missing ${required}. Google Analytics asks for utm_source, utm_medium and utm_campaign on every tagged URL; without them the visit lands in (not set).`,
      })
    }
  }

  const families = new Set<MacroFamily>()
  let platforms: Set<string> | null = null
  for (const row of filled) {
    const literal = segments(row.value.trim())
      .filter(seg => !seg.macro)
      .map(seg => seg.text)
      .join('')
    if (!options.lowercase && /[A-Z]/.test(literal) && /[a-z]/.test(literal)) {
      warnings.push({
        level: 'warn',
        message: `${row.key} mixes upper and lower case (“${literal.trim()}”). Values are case-sensitive, so “Spring_Sale” and “spring_sale” report as two campaigns.`,
      })
    }
    if (options.spaces === 'encode' && /\s/.test(literal.trim())) {
      warnings.push({
        level: 'warn',
        message: `${row.key} contains spaces. They will be sent as %20 — valid, but easy to mistype later and they read badly in reports.`,
      })
    }
    for (const macro of findMacros(row.value)) {
      families.add(macroFamily(macro))
      const owners = MACRO_OWNERS.get(macroName(macro))
      if (owners) {
        const current: Set<string> = platforms ?? new Set(owners)
        platforms = new Set([...current].filter(id => owners.has(id)))
      }
    }
  }
  if (families.size > 1 || (platforms !== null && platforms.size === 0)) {
    warnings.push({
      level: 'warn',
      message:
        'The values mix macro syntaxes from different ad platforms. Each platform only fills in its own macros; the others reach your analytics as literal text.',
    })
  }
  if (GA4_EXTRA_UTM_KEYS.slice(2).some(key => seen.has(key))) {
    warnings.push({
      level: 'info',
      message:
        'Google lists utm_creative_format and utm_marketing_tactic as accepted, but says neither is currently reported in Google Analytics properties.',
    })
  }

  const newPairs = filled.map(
    row => `${encodeURIComponent(row.key)}=${encodeValue(normalizeValue(row.value, options))}`,
  )
  const query = newPairs.join('&')

  if (!base) {
    return { url: '', query, warnings }
  }

  // --- the base URL -------------------------------------------------------
  const split = splitUrl(base)
  const { pairs, fragment } = split
  let path = split.path

  if (/\s/.test(path)) {
    warnings.push({
      level: 'error',
      message: 'The landing page URL contains a space. Remove it or replace it with %20 before using the link.',
    })
  }
  if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(path)) {
    path = `https://${path.replace(/^\/+/, '')}`
    warnings.push({
      level: 'info',
      message: 'Added https:// to the landing page. Ad platforms reject destination URLs without a scheme.',
    })
  }
  const scheme = /^([a-z][a-z0-9+.-]*):\/\/([^/?#]*)/i.exec(path)
  if (!scheme || !/^https?$/i.test(scheme[1]) || !/^[^@\s:]+(:\d+)?$/.test(scheme[2] ?? '')) {
    warnings.push({
      level: 'error',
      message: 'The landing page must be an http:// or https:// address with a domain, such as https://example.com/page.',
    })
    return { url: '', query, warnings }
  }

  const newKeys = new Set(filled.map(row => row.key.toLowerCase()))
  const existingCounts = new Map<string, number>()
  for (const pair of pairs) {
    const key = pairKey(pair)
    existingCounts.set(key, (existingCounts.get(key) ?? 0) + 1)
  }
  for (const [key, count] of existingCounts) {
    if (count > 1 && !newKeys.has(key)) {
      warnings.push({
        level: 'error',
        message: `The landing page already repeats ${key} ${count} times. Remove the extra copy.`,
      })
    }
  }
  const replaced = [...existingCounts.keys()].filter(key => newKeys.has(key))
  if (replaced.length > 0) {
    warnings.push({
      level: 'warn',
      message: `The landing page already had ${replaced.join(', ')}. The values above replace ${replaced.length === 1 ? 'it' : 'them'} so each key appears once.`,
    })
  }
  const keptUtm = [...existingCounts.keys()].filter(
    key => key.startsWith('utm_') && !newKeys.has(key),
  )
  if (keptUtm.length > 0) {
    warnings.push({
      level: 'warn',
      message: `The landing page already carries ${keptUtm.join(', ')}, which is kept as is. Check it belongs to this campaign.`,
    })
  }
  // `msclkid={msclkid}` in a tracking template is a macro, not a copied click.
  const clickIds = [
    ...new Set(
      pairs
        .filter(pair => {
          if (!CLICK_ID_KEYS.includes(pairKey(pair))) return false
          const eq = pair.indexOf('=')
          const value = eq === -1 ? '' : safeDecode(pair.slice(eq + 1)).trim()
          return value.length > 0 && value.replace(MACRO_PATTERN, '').length > 0
        })
        .map(pairKey),
    ),
  ]
  if (clickIds.length > 0) {
    warnings.push({
      level: 'error',
      message: `The landing page contains ${clickIds.join(', ')} — a click ID copied from a real ad click. Remove it, or every visit from this link is credited to that one old click.`,
    })
  }
  if (fragment) {
    warnings.push({
      level: 'info',
      message: `Kept ${fragment} at the end. Parameters have to sit before the #, because nothing after it is sent to the server.`,
    })
  }

  const keptPairs = pairs.filter(pair => !newKeys.has(pairKey(pair)))
  const allPairs = [...keptPairs, ...newPairs]
  const url = `${path}${allPairs.length > 0 ? `?${allPairs.join('&')}` : ''}${fragment}`

  return { url, query, warnings }
}
